// Local: src/components/Products/Financeiro/pages/MetasPage.jsx

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
  Alert,
} from '@mui/material';
import {
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  AttachMoney as AttachMoneyIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrackChanges as TrackChangesIcon,
  Scale as ScaleIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { 
  getContratos, 
  getMetas, 
  updateMetas 
} from '../services/api';

// Valores default
const defaultMetas = {
  META_ANUAL_CLIENTES: 0,
  META_MENSAL_CONTRATOS: 0,
  INVESTIMENTO_MENSAL: 0,
  MENSALIDADE_ATUAL: 0,
};

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // Normaliza para UTC para evitar problemas de fuso horário nos cálculos
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Componente de Card de KPI
const KpiCard = ({ title, value, icon, color = 'text.primary', subtitle = null }) => (
  <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ p: 1.5, bgcolor: `${color}.light`, color: `${color}.dark`, borderRadius: '50%' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ color: `${color}.dark` }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  </Paper>
);

const MetasPage = () => {
  const theme = useTheme();
  const [contratos, setContratos] = useState([]);
  const [metas, setMetas] = useState(null); 
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingMetas, setEditingMetas] = useState(defaultMetas);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const [contratosRes, metasRes] = await Promise.all([
          getContratos(),
          getMetas()
        ]);
        
        setContratos(Array.isArray(contratosRes) ? contratosRes : []);

        const mappedMetas = {
          META_ANUAL_CLIENTES: metasRes.meta_anual_clientes || 0,
          META_MENSAL_CONTRATOS: metasRes.meta_mensal_contratos || 0,
          INVESTIMENTO_MENSAL: metasRes.investimento_mensal || 0,
          MENSALIDADE_ATUAL: metasRes.mensalidade_atual || 0,
        };
        setMetas(mappedMetas);
        setEditingMetas(mappedMetas); 
      } catch (e) {
        console.error('Erro ao carregar dados do dashboard:', e);
        setContratos([]);
        setMetas(defaultMetas); 
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []); 

  // --- Constantes e Memos ---
  const now = new Date();
  const curYear = now.getUTCFullYear();
  const curMonth = now.getUTCMonth();

  const { META_ANUAL_CLIENTES, INVESTIMENTO_MENSAL, MENSALIDADE_ATUAL } = metas || defaultMetas;

  // --- AJUSTE NA LÓGICA DE CÁLCULO ---

  // PASSO 1: Cria um memo com a *lista* de contratos ativos
  const contratosAtivos = useMemo(() => {
    const activeStatuses = new Set([
      'ativo', 'trial', 'inadimplente', 'andamento', 'em andamento', 'ativo - gap', 'ativo_gap', 'ativo gap', 'ativo(a)',
    ]);
    return contratos.filter((contrato) => {
      const status = (contrato?.status || '').toString().trim().toLowerCase();
      if (!status) return true; // Assume ativo se o status estiver em branco
      if (['cancelado', 'cancelada', 'inativo', 'inativa', 'encerrado', 'finalizado'].includes(status)) return false;
      return activeStatuses.has(status);
    });
  }, [contratos]);

  // PASSO 2: Calcula o número de clientes a partir do memo acima
  const clientesAtivos = contratosAtivos.length;

  // PASSO 3: Calcula o MRR REAL somando o valor_mensal do memo acima
  const receitaRecorrenteEstimada = useMemo(() => {
    return contratosAtivos.reduce((acc, contrato) => {
      return acc + Number(contrato.valor_mensal || 0);
    }, 0);
  }, [contratosAtivos]); // MRR (Receita Mensal)

  // PASSO 4: Calcula o ARR baseado no MRR Real
  const receitaRecorrenteAnual = receitaRecorrenteEstimada * 12; // ARR

  // --- FIM DO AJUSTE ---

  // Filtra novos contratos no mês
  const contratosNovosMes = useMemo(() => {
    return contratos.reduce((acc, contrato) => {
      const inicio = parseDate(contrato?.data_inicio); 
      if (!inicio) return acc;
      
      if (inicio.getUTCFullYear() !== curYear || inicio.getUTCMonth() !== curMonth) return acc;
      return acc + 1;
    }, 0);
  }, [contratos, curYear, curMonth]);

  // Filtra churn (cancelados) no mês
  const churnMes = useMemo(() => {
    const statusCancelado = new Set(['cancelado', 'cancelada', 'inativo', 'inativa']);
    
    return contratos.reduce((acc, contrato) => {
      const status = (contrato?.status || '').toString().trim().toLowerCase();
      if (!statusCancelado.has(status)) return acc;

      const dataCancelamento = parseDate(contrato?.data_fim); 
      if (!dataCancelamento) return acc;
      
      if (dataCancelamento.getUTCFullYear() !== curYear || dataCancelamento.getUTCMonth() !== curMonth) return acc;
      return acc + 1;
    }, 0);
  }, [contratos, curYear, curMonth]);
  
  // --- Lógica de Cálculo dos Gráficos ---
  const analyticsData = useMemo(() => {
    const data = [];
    const today = new Date();
    const statusCancelado = new Set(['cancelado', 'cancelada', 'inativo', 'inativa']);

    const processedContratos = contratos.map(c => ({
      data_inicio: parseDate(c.data_inicio),
      data_fim: parseDate(c.data_fim),
      status: (c.status || '').toString().trim().toLowerCase()
    }));

    for (let i = 11; i >= 0; i--) {
      const targetDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i, 1));
      const year = targetDate.getUTCFullYear();
      const month = targetDate.getUTCMonth();
      const mesFim = new Date(Date.UTC(year, month + 1, 0)); 

      const mesLabel = targetDate.toLocaleString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', '');

      let novos = 0;
      let churn = 0;
      let acumulado = 0;

      for (const c of processedContratos) {
        if (c.data_inicio && c.data_inicio.getUTCFullYear() === year && c.data_inicio.getUTCMonth() === month) {
          novos++;
        }
        
        if (c.data_fim && statusCancelado.has(c.status) && c.data_fim.getUTCFullYear() === year && c.data_fim.getUTCMonth() === month) {
          churn++;
        }

        if (c.data_inicio && c.data_inicio <= mesFim) {
          if (!c.data_fim || c.data_fim > mesFim) {
            acumulado++; 
          } else if (c.data_fim <= mesFim && !statusCancelado.has(c.status)) {
            acumulado++; 
          }
        }
      }

      data.push({
        mes: mesLabel,
        novos: novos,
        churn: churn,
        acumulado: acumulado,
      });
    }
    return data;
  }, [contratos]); 


  // Prepara dados para o gráfico de Pacing (Ritmo)
  const pacingData = useMemo(() => {
    if (analyticsData.length === 0 || META_ANUAL_CLIENTES === 0) return [];
    
    const metaPorMes = META_ANUAL_CLIENTES / 12;
    return analyticsData.map((item, index) => ({
      ...item,
      meta: (metaPorMes * (index + 1)).toFixed(0),
      realizado: item.acumulado
    }));
  }, [analyticsData, META_ANUAL_CLIENTES]);

  // --- Funções do Modal ---
  const handleOpenEditModal = () => {
    setEditingMetas(metas); 
    setOpenEditModal(true);
  };
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
  };
  const handleChangeEditForm = (e) => {
    const { name, value } = e.target;
    setEditingMetas((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleSaveMetas = async () => {
    setIsSaving(true);
    try {
      await updateMetas(editingMetas); 
      setMetas(editingMetas); 
      handleCloseEditModal();
    } catch (err) {
      console.error("Erro ao salvar metas:", err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Cálculos para os novos cards ---
  const potencialARR = META_ANUAL_CLIENTES * MENSALIDADE_ATUAL * 12;
  const pontoDeEquilibrio = (MENSALIDADE_ATUAL > 0) ? (INVESTIMENTO_MENSAL / MENSALIDADE_ATUAL).toFixed(1) : "0.0";
  const margemDeCrescimento = receitaRecorrenteEstimada - INVESTIMENTO_MENSAL;
  const metaMensalCalculada = (META_ANUAL_CLIENTES > 0) ? (META_ANUAL_CLIENTES / 12).toFixed(1) : "0.0"; 

  return (
    <Box>
      {/* Título e Botão de Edição */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight={700}>Cockpit de Metas</Typography>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={handleOpenEditModal}>
          Editar Metas
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Análise de performance em tempo real (clientes e receita) e projeções baseadas em dados históricos.
      </Typography>

      {/* Nível 1: KPIs Imediatos */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={4} md={2.4}>
          <KpiCard title="Clientes Ativos" value={clientesAtivos} icon={<PeopleIcon />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <KpiCard title="MRR (Receita Mensal)" value={formatCurrency(receitaRecorrenteEstimada)} icon={<AttachMoneyIcon />} color="info" />
        </Grid>
        <Grid item xs={12} sm={4} md={2.4}>
          <KpiCard title="ARR (Receita Anual)" value={formatCurrency(receitaRecorrenteAnual)} icon={<TrendingUpIcon />} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard title="Novos Clientes (Mês)" value={contratosNovosMes} icon={<AssignmentTurnedInIcon />} color="secondary" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard title="Churn (Mês)" value={churnMes} icon={<TrendingDownIcon />} color="error" />
        </Grid>
      </Grid>

      {/* Nível 2: Análise de Trajetória (Gráficos) */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Funil de Crescimento (Últimos 12 Meses)</Typography>
            {analyticsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" maxHeight={300}>
                <BarChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="novos" name="Novos Clientes" fill={theme.palette.success.main} />
                  <Bar dataKey="churn" name="Clientes Perdidos" fill={theme.palette.error.main} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Alert severity="info">Não há dados históricos de contratos para exibir os gráficos.</Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Ritmo da Meta Anual (Clientes Acumulados)</Typography>
            {pacingData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" maxHeight={300}>
                <LineChart data={pacingData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="realizado" name="Clientes Reais" stroke={theme.palette.primary.main} strokeWidth={2} />
                  <Line type="monotone" dataKey="meta" name="Ritmo Ideal (Meta)" stroke={theme.palette.text.secondary} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Alert severity="info">Defina uma meta anual e tenha dados históricos para ver o ritmo.</Alert>
            )}
          </Paper>
        </Grid>

        {/* Nível 3: Análise Estratégica (Visual Novo) */}
        <Grid item xs={12} mt={3}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Análise Estratégica e Metas
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Potencial de ARR (Meta)"
                value={formatCurrency(potencialARR)}
                icon={<TrackChangesIcon />}
                color="success"
                subtitle={`Baseado na meta de ${META_ANUAL_CLIENTES} clientes.`}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Ponto de Equilíbrio"
                value={`${pontoDeEquilibrio} clientes`}
                icon={<ScaleIcon />}
                color="warning"
                subtitle={`Para cobrir ${formatCurrency(INVESTIMENTO_MENSAL)} de investimento.`}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Margem de Crescimento"
                value={formatCurrency(margemDeCrescimento)}
                icon={<AccountBalanceWalletIcon />}
                color="info"
                subtitle={`MRR Atual (${formatCurrency(receitaRecorrenteEstimada)}) - Investimento.`}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Ritmo Mensal Necessário"
                value={`${metaMensalCalculada} clientes/mês`}
                icon={<FlagIcon />}
                color="secondary"
                subtitle={`Para atingir a meta anual.`}
              />
            </Grid>
          </Grid>
        </Grid>
        
      </Grid>

      {/* Modal de Edição */}
      <Dialog open={openEditModal} onClose={handleCloseEditModal} disableRestoreFocus>
        <DialogTitle>Editar Metas e Valores Base</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Meta Anual de Clientes"
              name="META_ANUAL_CLIENTES"
              type="number"
              value={editingMetas.META_ANUAL_CLIENTES}
              onChange={handleChangeEditForm}
              fullWidth
            />
            <TextField
              label="Meta Mensal de Contratos"
              name="META_MENSAL_CONTRATOS"
              type="number"
              value={editingMetas.META_MENSAL_CONTRATOS}
              onChange={handleChangeEditForm}
              fullWidth
            />
            <TextField
              label="Investimento Mensal Previsto"
              name="INVESTIMENTO_MENSAL"
              type="number"
              value={editingMetas.INVESTIMENTO_MENSAL}
              onChange={handleChangeEditForm}
              fullWidth
            />
            <TextField
              label="Mensalidade Padrão"
              name="MENSALIDADE_ATUAL"
              type="number"
              value={editingMetas.MENSALIDADE_ATUAL}
              onChange={handleChangeEditForm}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} disabled={isSaving}>Cancelar</Button>
          <Button onClick={handleSaveMetas} variant="contained" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MetasPage;
